import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./Dashboard/DashboardLayout";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession, setSelectedAdminSession } from "./utils/adminSession";

const API = "/backend/public/api";

const pageStyle = {
  display: "grid",
  gap: "22px",
};

const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 50%, #c2410c 100%)",
  color: "#fff",
  borderRadius: "28px",
  padding: "28px",
  boxShadow: "0 30px 60px rgba(78, 27, 36, 0.24)",
  position: "relative",
  overflow: "hidden",
};

const heroGlowStyle = {
  position: "absolute",
  inset: "auto -60px -90px auto",
  width: "230px",
  height: "230px",
  background: "radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 70%)",
};

const heroSideCardStyle = {
  background: "rgba(255,255,255,0.14)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: "18px",
  padding: "14px 16px",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
};

const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "18px",
};

const statCardStyle = {
  background: "rgba(255,255,255,0.94)",
  borderRadius: "22px",
  padding: "20px",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  boxShadow: "0 18px 34px rgba(15, 23, 42, 0.08)",
};

const panelStyle = {
  background: "#ffffff",
  borderRadius: "24px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.14)",
  boxShadow: "0 20px 40px rgba(15, 23, 42, 0.07)",
};

const actionButtonStyle = {
  border: "none",
  borderRadius: "16px",
  padding: "14px 16px",
  fontWeight: 700,
  color: "#0f172a",
  background: "#f8fafc",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  width: "100%",
  textAlign: "left",
  boxShadow: "inset 0 0 0 1px rgba(148, 163, 184, 0.14)",
};

const toneMap = {
  blue: {
    iconBg: "rgba(59, 130, 246, 0.14)",
    iconColor: "#2563eb",
    accent: "#dbeafe",
  },
  emerald: {
    iconBg: "rgba(16, 185, 129, 0.14)",
    iconColor: "#059669",
    accent: "#d1fae5",
  },
  amber: {
    iconBg: "rgba(245, 158, 11, 0.14)",
    iconColor: "#d97706",
    accent: "#fef3c7",
  },
  rose: {
    iconBg: "rgba(244, 63, 94, 0.14)",
    iconColor: "#e11d48",
    accent: "#ffe4e6",
  },
};

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString("en-IN")}`;
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [currentSession, setCurrentSession] = useState(() => getSelectedAdminSession());
  const [activeSessionName, setActiveSessionName] = useState(() => localStorage.getItem("activeAcademicSession") || "");
  const [sessionRecords, setSessionRecords] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("adminDashboardSessionRecords") || "[]");
    } catch (error) {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [sessionMenuOpen, setSessionMenuOpen] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const sessionMenuRef = useRef(null);
  const currentSessionRef = useRef(currentSession);

  useEffect(() => {
    currentSessionRef.current = currentSession;
  }, [currentSession]);

  useEffect(() => {
    let active = true;

    const loadSessionsFast = async () => {
      try {
        const res = await axios.get(`${API}/academic-sessions`, {
          params: {
            _ts: Date.now(),
          },
        });
        if (!active) return;
        const rows = Array.isArray(res.data?.sessions) ? res.data.sessions : [];
        if (rows.length) {
          setSessionRecords(rows);
          localStorage.setItem("adminDashboardSessionRecords", JSON.stringify(rows));
        }
        const activeName = res.data?.active_session || rows.find((item) => Number(item.is_active) === 1)?.session_name || "";
        if (activeName) {
          setActiveSessionName(activeName);
          localStorage.setItem("activeAcademicSession", activeName);
          setCurrentSession((previous) => (previous && previous !== "All Sessions" ? previous : activeName));
        }
      } catch (error) {
        if (!active) return;
      }
    };

    const loadData = async (sessionName = (() => {
      const active = getSelectedAdminSession();
      return active && active !== "All Sessions" ? active : "";
    })()) => {
      try {
        setLoading(true);
        const statsRes = await axios.get(`${API}/dashboard`, {
          params: {
            ...(sessionName ? { session: sessionName } : {}),
            _ts: Date.now(),
          },
        });
        const [contactsRes, sessionRes] = await Promise.all([
          axios.get(`${API}/contacts`).catch(() => ({ data: [] })),
          axios.get(`${API}/academic-sessions`, {
            params: {
              _ts: Date.now(),
            },
          }).catch(() => ({ data: {} })),
        ]);

        if (!active) return;

        const sessionRows = (sessionRes.data?.sessions?.length ? sessionRes.data.sessions : statsRes.data?.available_sessions) || [];
        const activeName = sessionRes.data?.active_session || sessionRows.find((item) => Number(item.is_active) === 1)?.session_name || "";
        const nextSession = statsRes.data?.selected_session || sessionName || activeName || "All Sessions";

        setStats(statsRes.data || {});
        setContacts(contactsRes.data || []);
        setSessionRecords(sessionRows);
        localStorage.setItem("adminDashboardSessionRecords", JSON.stringify(sessionRows));
        setActiveSessionName(activeName);
        setCurrentSession(nextSession);
        if (activeName) {
          localStorage.setItem("activeAcademicSession", activeName);
        }
        localStorage.setItem("adminDashboardStats", JSON.stringify(statsRes.data || {}));
        window.dispatchEvent(new Event("admin-dashboard-stats-updated"));
        setLastUpdatedAt(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      } catch (error) {
        if (!active) return;
        setStats({});
        setContacts([]);
        setSessionRecords([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadSessionsFast();
    loadData();
    const liveTimer = window.setInterval(() => {
      loadData(currentSessionRef.current || getSelectedAdminSession());
    }, 60000);

    const reloadOnVisibility = () => {
      if (document.visibilityState === "visible") {
        loadData();
      }
    };

    const reloadOnFocus = () => {
      loadData(currentSessionRef.current || getSelectedAdminSession());
    };

    const reloadOnSessionChange = () => {
      loadData();
    };

    const reloadOnStorageChange = (event) => {
      if (event.key === "activeAcademicSession") {
        loadData(event.newValue || "All Sessions");
      }
    };

    window.addEventListener("focus", reloadOnFocus);
    document.addEventListener("visibilitychange", reloadOnVisibility);
    window.addEventListener("academic-session-changed", reloadOnSessionChange);
    window.addEventListener(ADMIN_SESSION_EVENT, reloadOnSessionChange);
    window.addEventListener("storage", reloadOnStorageChange);

    return () => {
      active = false;
      window.clearInterval(liveTimer);
      window.removeEventListener("focus", reloadOnFocus);
      document.removeEventListener("visibilitychange", reloadOnVisibility);
      window.removeEventListener("academic-session-changed", reloadOnSessionChange);
      window.removeEventListener(ADMIN_SESSION_EVENT, reloadOnSessionChange);
      window.removeEventListener("storage", reloadOnStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (sessionMenuRef.current && !sessionMenuRef.current.contains(event.target)) {
        setSessionMenuOpen(false);
      }
    };

    window.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const headlineCards = useMemo(
    () => [
      {
        label: "Registered Students",
        value: stats?.registered_students || 0,
        icon: "bi-people-fill",
        tone: "blue",
        detail: "Approved students already visible in View Student",
      },
      {
        label: "Franchise Applications",
        value: stats?.franchise_applications || 0,
        icon: "bi-diagram-3-fill",
        tone: "rose",
        detail: "Total new franchise applications received",
      },
      {
        label: "Courses",
        value: stats?.total_courses || 0,
        icon: "bi-journal-richtext",
        tone: "emerald",
        detail: "Total courses currently created in admin",
      },
      {
        label: "Messages",
        value: stats?.total_message || 0,
        icon: "bi-chat-dots-fill",
        tone: "amber",
        detail: "Contact and support messages",
      },
      {
        label: "Certified Students",
        value: stats?.certified_students || 0,
        icon: "bi-award-fill",
        tone: "emerald",
        detail: "Students certified from exam approval flow",
      },
      {
        label: "Pending Franchise",
        value: stats?.pending_franchise_applications || 0,
        icon: "bi-hourglass-split",
        tone: "rose",
        detail: "Franchise applications waiting for action",
      },
    ],
    [stats]
  );

  const financeCards = useMemo(
    () => [
      { label: "Total Deposit", value: formatMoney(stats?.total_deposit), icon: "bi-wallet2", tone: "emerald" },
      { label: "Total Deduct", value: formatMoney(stats?.total_deduct), icon: "bi-cash-stack", tone: "rose" },
      { label: "Total Payment", value: formatMoney(stats?.total_payment), icon: "bi-credit-card-2-front", tone: "blue" },
    ],
    [stats]
  );

  const quickActions = [
    { label: "Franchise Applications", helper: "Review and approve new centres", icon: "bi-building-add", path: "/dashboard/franchise-application" },
    { label: "View Franchise", helper: "Check approved centre list", icon: "bi-buildings", path: "/dashboard/view-franchise" },
    { label: "Student Applications", helper: "Review student applications", icon: "bi-person-vcard", path: "/dashboard/view-application" },
    { label: "State & District", helper: "Update region settings", icon: "bi-geo-alt", path: "/dashboard/state" },
  ];

  const dashboardSessionRecords = useMemo(
    () => (sessionRecords.length ? sessionRecords : stats?.available_sessions || []),
    [sessionRecords, stats?.available_sessions]
  );

  const sessionOptions = useMemo(() => {
    const names = dashboardSessionRecords
      .map((item) => item.session_name)
      .filter((item, index, array) => item && array.indexOf(item) === index && item !== "All Sessions");

    return ["All Sessions", ...names];
  }, [dashboardSessionRecords]);

  const selectedSessionStatus = useMemo(() => {
    if (currentSession === "All Sessions") {
      return { label: "All Sessions", icon: "bi-layers-fill", bg: "rgba(255,255,255,0.16)", color: "#fff7ed" };
    }

    const row = dashboardSessionRecords.find((item) => String(item.session_name).trim() === String(currentSession).trim());
    if (Number(row?.is_active) === 1 || String(currentSession).trim() === String(activeSessionName).trim()) {
      return { label: "Active", icon: "bi-check-circle-fill", bg: "rgba(22, 163, 74, 0.22)", color: "#dcfce7" };
    }

    return { label: "Inactive", icon: "bi-pause-circle-fill", bg: "rgba(255,255,255,0.16)", color: "#fde68a" };
  }, [activeSessionName, currentSession, dashboardSessionRecords]);

  const studentProgress = useMemo(() => {
    const total = Number(stats?.student_scope_total || 0);
    const registered = Number(stats?.registered_students || 0);
    const accepted = Number(stats?.accepted_students || 0);
    const applied = Number(stats?.applied_students || 0);

    return [
      { label: "Registered Students", value: registered, percent: total ? Math.round((registered / total) * 100) : 0, color: "#2563eb" },
      { label: "Accepted Students", value: accepted, percent: total ? Math.round((accepted / total) * 100) : 0, color: "#059669" },
      { label: "Applied Students", value: applied, percent: total ? Math.round((applied / total) * 100) : 0, color: "#d97706" },
    ];
  }, [stats]);

  const operationalMetrics = [
    { label: "Accepted Students", value: stats?.accepted_students || 0 },
    { label: "Applied Students", value: stats?.applied_students || 0 },
    { label: "Certified Students", value: stats?.certified_students || 0 },
    { label: "Online Results", value: stats?.total_online_result || 0 },
    { label: "Offline Exams", value: stats?.total_offline_exam || 0 },
    { label: "Franchise Centres", value: stats?.total_affiliation || 0 },
    { label: "Downloads", value: stats?.total_download || 0 },
    { label: "Open Tickets", value: stats?.tickets_open || 0 },
  ];

  const focusItems = useMemo(
    () => [
      {
        title: "Student approval queue",
        note: `${stats?.applied_students || 0} applied and ${stats?.accepted_students || 0} accepted student records in the selected scope.`,
        icon: "bi-person-check-fill",
        color: "#2563eb",
        bg: "#eff6ff",
      },
      {
        title: "Franchise review",
        note: `${stats?.pending_franchise_applications || 0} pending franchise applications and ${stats?.view_franchise || 0} live centres.`,
        icon: "bi-building-check",
        color: "#be123c",
        bg: "#fff1f2",
      },
      {
        title: "Course structure",
        note: `${stats?.total_courses || 0} courses, ${stats?.total_course_categories || 0} categories, and ${stats?.total_course_modules || 0} modules connected.`,
        icon: "bi-journal-code",
        color: "#059669",
        bg: "#ecfdf5",
      },
      {
        title: "Exam publishing",
        note: `${stats?.total_exam || 0} exams, ${stats?.total_online_result || 0} online results, and ${stats?.certified_students || 0} certified students.`,
        icon: "bi-patch-check-fill",
        color: "#d97706",
        bg: "#fffbeb",
      },
    ],
    [stats]
  );

  const activateSession = async (nextSession) => {
    if (nextSession === "All Sessions") {
      setCurrentSession("All Sessions");
      setSelectedAdminSession("All Sessions");
      setSessionMenuOpen(false);
      try {
        setLoading(true);
        const [statsRes, contactsRes, sessionRes] = await Promise.all([
          axios.get(`${API}/dashboard`, {
            params: {
              session: "All Sessions",
              _ts: Date.now(),
            },
          }),
          axios.get(`${API}/contacts`),
          axios.get(`${API}/academic-sessions`, {
            params: {
              _ts: Date.now(),
            },
          }),
        ]);
        const sessionRows = sessionRes.data?.sessions || statsRes.data?.available_sessions || [];
        const activeName = sessionRes.data?.active_session || sessionRows.find((item) => Number(item.is_active) === 1)?.session_name || "";

        setStats(statsRes.data || {});
        setContacts(contactsRes.data || []);
        setSessionRecords(sessionRows);
        localStorage.setItem("adminDashboardSessionRecords", JSON.stringify(sessionRows));
        setActiveSessionName(activeName);
        if (activeName) {
          localStorage.setItem("activeAcademicSession", activeName);
        }
        localStorage.setItem("adminDashboardStats", JSON.stringify(statsRes.data || {}));
        window.dispatchEvent(new Event("admin-dashboard-stats-updated"));
        setLastUpdatedAt(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      } catch (error) {
        window.alert(error.response?.data?.message || "Dashboard data load failed");
      } finally {
        setLoading(false);
      }
      return;
    }

    const sessionRow = dashboardSessionRecords.find((item) => String(item.session_name).trim() === String(nextSession).trim());
    if (!sessionRow) return;

    try {
      setLoading(true);
      const [statsRes, contactsRes, sessionRes] = await Promise.all([
        axios.get(`${API}/dashboard`, {
          params: {
            session: nextSession,
            _ts: Date.now(),
          },
        }),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/academic-sessions`, {
          params: {
            _ts: Date.now(),
          },
        }),
      ]);
      const sessionRows = sessionRes.data?.sessions || statsRes.data?.available_sessions || [];
      const activeName = sessionRes.data?.active_session || sessionRows.find((item) => Number(item.is_active) === 1)?.session_name || "";

      setStats(statsRes.data || {});
      setContacts(contactsRes.data || []);
      setSessionRecords(sessionRows);
      localStorage.setItem("adminDashboardSessionRecords", JSON.stringify(sessionRows));
      setActiveSessionName(activeName);
      if (activeName) {
        localStorage.setItem("activeAcademicSession", activeName);
      }
      localStorage.setItem("adminDashboardStats", JSON.stringify(statsRes.data || {}));
      window.dispatchEvent(new Event("admin-dashboard-stats-updated"));
      setLastUpdatedAt(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      const selectedName = statsRes.data?.selected_session || nextSession;
      setCurrentSession(selectedName);
      setSelectedAdminSession(selectedName);
      setSessionMenuOpen(false);
    } catch (error) {
      window.alert(error.response?.data?.message || "Dashboard session data load failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div style={pageStyle}>
        <section style={heroStyle}>
          <div style={heroGlowStyle} />
          <div style={{ position: "relative", zIndex: 1, display: "grid", gap: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 620px", maxWidth: "720px", minWidth: 0 }}>
                <p style={{ margin: 0, opacity: 0.78, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "12px", fontWeight: 700 }}>Master Admin Workspace</p>
                <h1 style={{ margin: "8px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>National Institute dashboard with live operational summary</h1>
                <p style={{ margin: 0, maxWidth: "640px", color: "rgba(255,255,255,0.82)", lineHeight: 1.75 }}>
                  Franchise approvals, student applications, finance status, messaging, and system health are organized here in one modern control room.
                </p>
              </div>

              <div style={{ width: "280px", maxWidth: "100%", display: "grid", gap: "10px", alignSelf: "flex-start", flexShrink: 0, marginLeft: "auto" }}>
                <div style={{ ...heroSideCardStyle, padding: "14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                    <div style={{ color: "rgba(255,255,255,0.76)", fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                      Session Control
                    </div>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "5px 9px",
                        borderRadius: "999px",
                        background: selectedSessionStatus.bg,
                        fontSize: "11px",
                        fontWeight: 700,
                        color: selectedSessionStatus.color,
                      }}
                    >
                      <i className={`bi ${selectedSessionStatus.icon}`}></i>
                      {selectedSessionStatus.label}
                    </span>
                  </div>
                  <div ref={sessionMenuRef} style={{ position: "relative" }}>
                    <button
                      type="button"
                      onClick={() => setSessionMenuOpen((prev) => !prev)}
                      style={{
                        width: "100%",
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,0.24)",
                        background: "rgba(255,255,255,0.14)",
                        color: "#ffffff",
                        fontWeight: 700,
                        padding: "11px 12px",
                        fontSize: "14px",
                        outline: "none",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
                        backdropFilter: "blur(10px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                      }}
                    >
                      <span>{currentSession}</span>
                      <i className={`bi ${sessionMenuOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                    </button>

                    {sessionMenuOpen ? (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 8px)",
                          left: 0,
                          right: 0,
                          borderRadius: "14px",
                          border: "1px solid rgba(255,255,255,0.22)",
                          background: "rgba(93, 27, 39, 0.84)",
                          backdropFilter: "blur(16px)",
                          boxShadow: "0 20px 40px rgba(15, 23, 42, 0.24)",
                          overflow: "hidden",
                          zIndex: 30,
                        }}
                      >
                        {sessionOptions.map((session) => {
                          const isActive = session === currentSession;
                          return (
                            <button
                              key={session}
                              type="button"
                              onClick={() => activateSession(session)}
                              style={{
                                width: "100%",
                                border: "none",
                                borderBottom: session === sessionOptions[sessionOptions.length - 1] ? "none" : "1px solid rgba(255,255,255,0.08)",
                                background: isActive ? "rgba(255,255,255,0.16)" : "transparent",
                                color: "#ffffff",
                                textAlign: "left",
                                padding: "11px 12px",
                                fontSize: "14px",
                                fontWeight: isActive ? 800 : 600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "10px",
                                cursor: "pointer",
                              }}
                            >
                              <span>{session}</span>
                              {isActive ? <i className="bi bi-check2"></i> : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ marginTop: "9px", color: "rgba(255,255,255,0.78)", fontSize: "11px", lineHeight: 1.6 }}>
                    Sob entry kora session ekhane dekhabe. Selected session-er actual status badge-e show hobe.
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div style={{ ...heroSideCardStyle, padding: "13px 14px" }}>
                    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "11px", fontWeight: 700 }}>Selected Session</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, marginTop: "8px", lineHeight: 1.4, color: "#ffffff" }}>
                      {currentSession}
                    </div>
                  </div>
                  <div style={{ ...heroSideCardStyle, padding: "13px 14px" }}>
                    <div style={{ color: "rgba(255,255,255,0.68)", fontSize: "11px", fontWeight: 700 }}>Students In Scope</div>
                    <div style={{ fontSize: "24px", fontWeight: 800, marginTop: "6px", color: "#ffffff" }}>
                      {stats?.student_scope_total || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button type="button" className="btn btn-light" onClick={() => navigate("/dashboard/franchise-application")}>
                Review Franchise
              </button>
              <button type="button" className="btn btn-outline-light" onClick={() => navigate("/dashboard/view-application")}>
                Open Student Queue
              </button>
              <button type="button" className="btn btn-outline-light" onClick={() => activateSession(currentSession)}>
                {loading ? "Refreshing..." : "Refresh Live Data"}
              </button>
              <span style={{ display: "inline-flex", alignItems: "center", padding: "8px 12px", borderRadius: "999px", background: "rgba(255,255,255,0.16)", color: "#ffffff", fontWeight: 700, fontSize: "12px" }}>
                Live sync: {lastUpdatedAt || "loading"}
              </span>
            </div>
          </div>
        </section>

        <section style={cardGridStyle}>
          {headlineCards.map((card) => {
            const tone = toneMap[card.tone];
            return (
              <div key={card.label} style={statCardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", alignItems: "flex-start" }}>
                  <div>
                    <p style={{ margin: 0, color: "#64748b", fontSize: "13px", fontWeight: 700 }}>{card.label}</p>
                    <h3 style={{ margin: "12px 0 6px", fontSize: "32px", color: "#0f172a" }}>{loading ? "..." : card.value}</h3>
                    <p style={{ margin: 0, color: "#94a3b8", fontSize: "12px" }}>{card.detail}</p>
                  </div>
                  <div style={{ width: "48px", height: "48px", borderRadius: "16px", background: tone.iconBg, color: tone.iconColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px" }}>
                    <i className={`bi ${card.icon}`}></i>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: "18px" }}>
          <div style={panelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "18px", flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>Session Student Snapshot</h3>
                <p style={{ margin: "6px 0 0", color: "#64748b" }}>Counts below follow the selected session and will drive future admission-wise filters.</p>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#eff6ff", color: "#1d4ed8", borderRadius: "999px", padding: "8px 12px", fontWeight: 700, fontSize: "12px" }}>
                <i className="bi bi-graph-up-arrow"></i>
                {currentSession}
              </div>
            </div>

            <div style={{ display: "grid", gap: "16px" }}>
              {studentProgress.map((item) => (
                <div key={item.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "8px" }}>
                    <span style={{ color: "#334155", fontWeight: 700 }}>{item.label}</span>
                    <span style={{ color: item.color, fontWeight: 800 }}>{item.value}</span>
                  </div>
                  <div style={{ height: "10px", background: "#e2e8f0", borderRadius: "999px", overflow: "hidden" }}>
                    <div style={{ width: `${item.percent}%`, height: "100%", background: item.color, borderRadius: "999px" }}></div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "22px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "14px" }}>
              {operationalMetrics.map((metric) => (
                <div key={metric.label} style={{ background: "#f8fafc", borderRadius: "18px", padding: "16px", border: "1px solid #e2e8f0" }}>
                  <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700 }}>{metric.label}</div>
                  <div style={{ marginTop: "8px", color: "#0f172a", fontSize: "24px", fontWeight: 800 }}>{loading ? "..." : metric.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gap: "18px" }}>
            <div style={panelStyle}>
              <h3 style={{ marginTop: 0, color: "#0f172a" }}>Quick Actions</h3>
              <p style={{ color: "#64748b", marginTop: "6px", marginBottom: "18px" }}>Jump straight into the most used admin workflows.</p>
              <div style={{ display: "grid", gap: "12px" }}>
                {quickActions.map((action) => (
                  <button key={action.label} type="button" style={actionButtonStyle} onClick={() => navigate(action.path)}>
                    <span style={{ width: "42px", height: "42px", borderRadius: "14px", background: "#eef2ff", color: "#4338ca", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>
                      <i className={`bi ${action.icon}`}></i>
                    </span>
                    <span>
                      <span style={{ display: "block", fontSize: "14px" }}>{action.label}</span>
                      <span style={{ display: "block", fontSize: "12px", color: "#64748b", fontWeight: 500 }}>{action.helper}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div style={panelStyle}>
              <h3 style={{ marginTop: 0, color: "#0f172a" }}>Finance Pulse</h3>
              <div style={{ display: "grid", gap: "12px", marginTop: "16px" }}>
                {financeCards.map((item) => {
                  const tone = toneMap[item.tone];
                  return (
                    <div key={item.label} style={{ background: tone.accent, borderRadius: "18px", padding: "16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                      <div>
                        <div style={{ color: "#334155", fontSize: "12px", fontWeight: 700 }}>{item.label}</div>
                        <div style={{ marginTop: "8px", color: "#0f172a", fontWeight: 800, fontSize: "22px" }}>{loading ? "..." : item.value}</div>
                      </div>
                      <div style={{ width: "44px", height: "44px", borderRadius: "14px", background: "#fff", color: tone.iconColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>
                        <i className={`bi ${item.icon}`}></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
          <div style={panelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "18px" }}>
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>Recent Messages</h3>
                <p style={{ margin: "6px 0 0", color: "#64748b" }}>Latest contact requests and user queries.</p>
              </div>
              <span style={{ display: "inline-flex", alignItems: "center", padding: "8px 12px", borderRadius: "999px", background: "#fff7ed", color: "#c2410c", fontWeight: 700, fontSize: "12px" }}>
                {contacts.length} items
              </span>
            </div>

            <div style={{ display: "grid", gap: "12px" }}>
              {contacts.length > 0 ? (
                contacts.slice(0, 4).map((contact) => (
                  <div key={contact.id} style={{ padding: "16px", border: "1px solid #e2e8f0", borderRadius: "18px", background: "#f8fafc" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
                      <strong style={{ color: "#0f172a" }}>{contact.name}</strong>
                      <span style={{ color: "#94a3b8", fontSize: "12px" }}>{contact.email}</span>
                    </div>
                    <p style={{ margin: 0, color: "#475569", lineHeight: 1.65 }}>{contact.message}</p>
                  </div>
                ))
              ) : (
                <div style={{ padding: "20px", borderRadius: "18px", background: "#f8fafc", color: "#64748b" }}>No recent contact messages available.</div>
              )}
            </div>
          </div>

          <div style={panelStyle}>
            <h3 style={{ marginTop: 0, color: "#0f172a" }}>Admin Focus Board</h3>
            <p style={{ color: "#64748b", marginTop: "6px" }}>Priority areas to keep the system healthy and responsive.</p>

            <div style={{ display: "grid", gap: "14px", marginTop: "18px" }}>
              {focusItems.map((item) => (
                <div key={item.title} style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "16px", borderRadius: "18px", background: item.bg }}>
                  <div style={{ width: "42px", height: "42px", borderRadius: "14px", background: "#fff", color: item.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>
                    <i className={`bi ${item.icon}`}></i>
                  </div>
                  <div>
                    <div style={{ color: "#0f172a", fontWeight: 800 }}>{item.title}</div>
                    <div style={{ color: "#475569", marginTop: "6px", lineHeight: 1.6, fontSize: "14px" }}>{item.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

export default Dashboard;



